/** Shared session lifecycle for both spread sessions and game sessions (§28). */
export const SESSION_STATUSES = ['waiting', 'active', 'paused', 'completed'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

export const SESSION_STATUS_LABELS: Record<SessionStatus, string> = {
  waiting: 'Ожидание',
  active: 'Идёт',
  paused: 'На паузе',
  completed: 'Завершена',
};

const ALLOWED_TRANSITIONS: Record<SessionStatus, readonly SessionStatus[]> = {
  waiting: ['active'],
  active: ['paused', 'completed'],
  paused: ['active', 'completed'],
  completed: [],
};

export function canTransition(from: SessionStatus, to: SessionStatus): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

export function nextStatuses(from: SessionStatus): readonly SessionStatus[] {
  return ALLOWED_TRANSITIONS[from];
}

/** Gameplay actions are only legal while the session is active (§28). */
export function allowsGameplay(status: SessionStatus): boolean {
  return status === 'active';
}

export const SESSION_KINDS = ['spread', 'game'] as const;
export type SessionKind = (typeof SESSION_KINDS)[number];

export const PARTICIPANT_KINDS = ['facilitator', 'guest', 'user'] as const;
export type ParticipantKind = (typeof PARTICIPANT_KINDS)[number];

/**
 * Кто сидит за столом и ходит.
 *
 * Ведущий ведёт партию; играют остальные. Обычно это гости по ссылке, но за
 * стол садится и сам ведущий — тогда он и ведёт, и играет, и его фишка ничем
 * не отличается от чужой. Поэтому «игрок» — это «не ведущий», а не «гость»:
 * иначе одиночная партия оказалась бы партией без игроков.
 */
export const PLAYER_PARTICIPANT_KINDS = ['guest', 'user'] as const;

export function isPlayerKind(kind: ParticipantKind): boolean {
  return kind !== 'facilitator';
}

/*
 * Хранение состояния сессий (политика, пп. 2.6 и 6.5; оферта, пп. 4.6–4.7):
 * постоянного архива нет. Приостановленная сессия хранится 45 календарных дней
 * с момента приостановления — повторный вход срок не продлевает, новое
 * приостановление после возобновления начинает его заново. Завершённая
 * удаляется сразу после завершения: час остаётся, чтобы участники увидели итог,
 * а ведущий мог отменить случайное завершение.
 */
export const PAUSED_SESSION_RETENTION_DAYS = 45;
export const COMPLETED_SESSION_GRACE_HOURS = 1;

/** «до 16 декабря 2026, 13:00» — когда сессия будет удалена, по Москве. */
export function formatSessionDeletion(iso: string): string {
  const date = new Date(iso);
  const day = date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Europe/Moscow' }).replace(/\s?г\.$/, '');
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' });
  return `${day}, ${time} мск`;
}

/** Когда сессия будет удалена; `null` — пока не удаляется (ожидание, идёт). */
export function sessionDeletionAt(session: {
  status: SessionStatus;
  pausedAt: string | null;
  completedAt: string | null;
}): string | null {
  if (session.status === 'paused' && session.pausedAt) {
    return new Date(Date.parse(session.pausedAt) + PAUSED_SESSION_RETENTION_DAYS * 24 * 60 * 60 * 1000).toISOString();
  }
  if (session.status === 'completed' && session.completedAt) {
    return new Date(Date.parse(session.completedAt) + COMPLETED_SESSION_GRACE_HOURS * 60 * 60 * 1000).toISOString();
  }
  return null;
}
